import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons'
import { useContext, useState, useEffect } from 'react'
import { useLazyGetOrganisationTypesQuery, usePostNotificationMutation } from '../../../../Redux/Api/Organisations';
import { useAppSelector } from '../../../../Redux/store'
import { Hydra } from '../../../../Utils/Hydra'
import { all, max, min } from '../../../../Utils/InValidation'
import BusyArea from '../../../Shared/BusyArea'
import Button from '../../../Shared/Form/Button'
import Form from '../../../Shared/Form/Form'
import Input, { FailedRequirements } from '../../../Shared/Form/Input'
import StatusPopups from '../../../Shared/Form/StatusPopups'
import { ModalContext } from '../../../Shared/Modal/Modal'
import Title from '../../../Shared/Title'
import Dropdown from "../../../Shared/Form/Dropdown";
import LoadingPage from "../../LoadingPage";
import { useTranslation } from "react-i18next";
import NotifyPreview from "./NotifyPreview";
import { useNavigate, useLocation } from 'react-router-dom';
import CalendarInput from '../../../Shared/Form/CalendarInput';
import HourInput from '../../../Shared/Form/HourInput';
import Card from '../../../Shared/Card/Card';
import PageMaxWidth from '../../../Shared/pageRenderers/PageMaxWidth';
import MobilePreviewWrapper from '../../../Shared/pageRenderers/MobilePreviewWrapper';
import { FormControlLabel } from '@mui/material';
import Checkbox from '../../../MuiComponentOverrides/Checkbox';

const NotifyPage = () => {

    const modalContext = useContext(ModalContext)
    const { t } = useTranslation()

    const org_id = useAppSelector((s) => s.organisation.id)!
    const [getTypes, { data: types }] = useLazyGetOrganisationTypesQuery()

    const [error, setError] = useState('')
    const [errorSubtitle, setErrorSubtitle] = useState('')
    const [submitted, setSubmitted] = useState(false);
    const [busy, setBusy] = useState(false)
    const [sendNotifications] = usePostNotificationMutation()
    const [message, setMessage] = useState('')
    const [sendType, setSendType] = useState(true)
    const [sendTime, setSendTime] = useState(true)
    const [refetch, setRefetch] = useState(false)

    const [date, setDate] = useState(new Date());

    const [title, setTitle] = useState('')
    const [content, setContent] = useState('')

    const location = useLocation();
    const { news } = location.state || {};

    const toMySQLDate = (date: string, time: string): string => {
        const parsedDate = new Date(date);

        const year = parsedDate.getFullYear();
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedDate.getDate()).padStart(2, '0');

        return `${year}-${month}-${day} ${time}`;
    }

    useEffect(() => {
        if (news) {
            setTitle(news.title)
            setContent(cleanData(news.text))
            news.type && setSendType(news.type !== 'all' ? false : true)
        }
    }, [news])

    useEffect(() => {
        if (org_id)
            getTypes(org_id)
    }, [org_id])

    const handleSendType = () => {
        if (!types) return;

        if (types.length > 0) {
            setSendType(!sendType)
        } else {
            setError(t('modal:noTypes') as string)
            setErrorSubtitle('\n')
        }
    }

    const handleSendTime = () => {
        setSendTime(!sendTime)
    }

    const submit = async (data: { title: string, content: string, url: string, cardType: string, scheduledAt: string, scheduledAtTime: string }) => {
        setSubmitted(true)
        setError('')
        setErrorSubtitle('')
        if (busy) return
        if (FailedRequirements(data, 'title', 'content')) return setError(t('modal:error') as string)
        if (data.scheduledAt && data.scheduledAtTime.length !== 5) return setError(t('modal:error') as string)

        if (data.url !== "") {
            if (!data.url.toLowerCase().includes('http')) {
                setError(t('modal:urlError') as string)
                setBusy(false)
                return;
            }
        }

        if (!(await modalContext.show(t('modal:attention') as string, t('modal:notification') as string))) {
            modalContext.hide()
            return
        }

        modalContext.hide()

        setBusy(true)
        setError('')
        setMessage('')


        try {
            await sendNotifications({
                id: org_id,
                title: data.title,
                body: data.content,
                url: data.url,
                type: data.cardType,
                scheduledAt: data.scheduledAt ? toMySQLDate(data.scheduledAt, data.scheduledAtTime) : null
            })

            if (data.cardType === 'all') {
                const sentMessage = t('notification:sent');
                setMessage(sentMessage);
            } else {
                const sentMessage = t('notification:sentType');
                setMessage(sentMessage);
            }

            setTitle('');
            setContent('');
            setRefetch(!refetch);

        } catch (e) {
            let err = e as Hydra.Error
            setError(err['hydra:description'])
            setBusy(false)
        }

        setBusy(false)
    }

    const cleanData = (data: string) => {
        return data.replace(/<\/(h1|h2|h3|h4|h5|h6|p|div|li)>/gi, '\n').replace(/<\/?[^>]+(>|$)/g, '');
    }

    if (!types) return <LoadingPage />

    return (
        <PageMaxWidth pageType='form'>
            <BusyArea busy={busy}>
                <MobilePreviewWrapper mobileNode={<NotifyPreview title={title} content={content} customDate={sendTime} />} >
                    <Title text={t('notification:title')} subtitle={t('notification:subtitle') as string} />
                    <StatusPopups setText={setError} setSubtitle={setErrorSubtitle} type='error' text={error} subtitle={errorSubtitle} />
                    <StatusPopups setText={setMessage} type='success' text={message} />
                    <Form submit={submit}>
                        <Input
                            required
                            submitted={submitted}
                            invalidator={all(min(3), max(100))}
                            label={t('notification:tableTitle')}
                            id='title'
                            onChange={(e) => setTitle((e.target as HTMLInputElement).value)}
                            initial={news ? news.title : ''}
                        />

                        <Input
                            required
                            submitted={submitted}
                            large
                            invalidator={all(min(3), max(250))}
                            label={t('notification:message')}
                            id='content'
                            onChange={(e) => setContent((e.target as HTMLInputElement).value)}
                            initial={news ? cleanData(news.text) : ''}
                        />

                        <Card outline rounded className='mb-6'>
                            <FormControlLabel
                                label={t('notification:checkbox')}
                                control={
                                    <Checkbox
                                        onChange={handleSendType}
                                        checked={sendType}
                                    />
                                }
                            />
                            {types.length < 0 && (
                                <p className='text-sm text-gray-400 mt-1'>({t('modal:noTypes')})</p>
                            )}
                            {sendType ? (
                                <input type='hidden' value='all' name='cardType' />
                            ) : (
                                <Dropdown noMarginBottom className='mt-2 mb-2' label={t('notification:type')} initial={news ? news.type : ''} id='cardType' options={
                                    Object.fromEntries(types.map(type => ([type.id, type.name])))
                                } />
                            )}
                        </Card>

                        <Card outline rounded className='mb-6'>
                            <FormControlLabel
                                label={t('notification:checkboxTime')}
                                control={
                                    <Checkbox
                                        onChange={handleSendTime}
                                        checked={sendTime}
                                    />
                                }
                            />
                            {!sendTime && (
                                <div className='mt-6'>
                                    <CalendarInput
                                        required
                                        submitted={submitted}
                                        id='scheduledAt'
                                        label={t('notification:sendAtDate')}
                                        sublabel={t('notification:sendAtDateSubtitle') as string}
                                        className='mb-6'
                                        min={new Date(new Date().toISOString().split('T')[0])}
                                        setDate={setDate}
                                    />
                                    <HourInput
                                        required
                                        submitted={submitted}
                                        id='scheduledAtTime'
                                        label={t('notification:sendAtTime')}
                                        isToday={date}
                                    />
                                </div>
                            )}
                        </Card>

                        <Input
                            invalidator={all(min(3), max(250))}
                            label={t('notification:url')}
                            sublabel={t('notification:optional') as string}
                            type='url'
                            placeholder='https://'
                            id='url'
                            initial={news ? news.url : ''}
                        />

                        <Button title={t('notification:send')} iconright icon={faPaperPlane} />
                    </Form>
                </MobilePreviewWrapper>
            </BusyArea>
        </PageMaxWidth>
    )
}

export default NotifyPage;
